import { useState, useRef, useEffect } from "react";
import { Users } from "lucide-react";

/**
 * PeerBenchmarkChip
 *
 * Compact hover chip that surfaces "You: X / Peer Median: Y" for any metric
 * that has a mapping in the OnPoint FRB comparison endpoint.
 *
 * Palette (Section 01 Rule 15): Gold when above peer median, Blue when at
 * median, Orange when below - never red/green.
 */
export default function PeerBenchmarkChip({ band, metricKey, compact = false }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  if (!band || band.median === null || band.median === undefined) return null;
  const isNumber = (v) => typeof v === "number" && !Number.isNaN(v);
  if (!isNumber(band.company)) return null;

  const fmt = (v) => {
    if (!isNumber(v)) return "-";
    if (band.unit === "%") return `${v.toFixed(1)}%`;
    if (band.unit === "days") return `${v.toFixed(0)}d`;
    return `${v.toFixed(2)}x`;
  };

  const higherBetter = !!band.higher_is_better;
  const median = band.median;
  const beatsMedian = higherBetter ? band.company >= median : band.company <= median;
  const nearMedian = Math.abs(band.company - median) / (median || 1) < 0.05;

  let bg = "#DBE6F4";
  let color = "#1B75BA";
  let label = "vs peer";
  if (nearMedian) {
    bg = "#DBE6F4"; color = "#1B75BA"; label = "at median";
  } else if (beatsMedian) {
    bg = "#FEF3E6"; color = "#A16207"; label = "beats peers";
  } else {
    bg = "#FFEDD5"; color = "#C2410C"; label = "below peers";
  }

  const openWith = () => { timer.current && clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), 120); };
  const closeWith = () => { timer.current && clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={openWith}
      onMouseLeave={closeWith}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      role="button"
      aria-label={`Peer benchmark: you ${fmt(band.company)} vs peer median ${fmt(median)}. ${label}.`}
      data-testid={`peer-chip-${metricKey}`}
    >
      <span
        className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: bg, color, border: `1px solid ${color}` }}
      >
        <Users size={9} />
        {compact ? "PEER" : label.toUpperCase()}
      </span>

      {open && (
        <span
          className="absolute z-40 top-full left-0 mt-1 rounded-lg shadow-lg p-2.5 text-[11px]"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E8EAF0", minWidth: 220, boxShadow: "0 4px 12px rgba(26,32,44,0.10)" }}
          data-testid={`peer-chip-popover-${metricKey}`}
          role="tooltip"
        >
          <div className="font-bold mb-1" style={{ color: "#1C2040" }}>{band.label || metricKey}</div>
          <div className="grid grid-cols-2 gap-1">
            <span style={{ color: "#6B7491" }}>You:</span>
            <span className="text-right font-mono" style={{ color: "#1C2040" }}>{fmt(band.company)}</span>
            <span style={{ color: "#6B7491" }}>Peer Low:</span>
            <span className="text-right font-mono" style={{ color: "#6B7491" }}>{fmt(band.low)}</span>
            <span style={{ color: "#6B7491" }}>Peer Median:</span>
            <span className="text-right font-mono font-bold" style={{ color: "#1B75BA" }}>{fmt(median)}</span>
            <span style={{ color: "#6B7491" }}>Peer High:</span>
            <span className="text-right font-mono" style={{ color: "#6B7491" }}>{fmt(band.high)}</span>
          </div>
          <div className="mt-1.5 pt-1.5 text-[10px]" style={{ borderTop: "1px dashed #E8EAF0", color: "#4B5570" }}>
            {band.verdict}
          </div>
          <div className="mt-1 text-[9px]" style={{ color: "#9BA4B8" }}>ProSight / RMA FRB peer bands - {higherBetter ? "higher is better" : "lower is better"}.</div>
        </span>
      )}
    </span>
  );
}
